import React, {
  useState,
  useEffect
} from 'react';
import moment from 'moment';

import { makeStyles } from '@material-ui/core/styles';
import {
  Paper,
  Chip,
  Grid,
  Typography,
  Divider,
  Button,
} from '@material-ui/core/';
import CloudDownloadIcon from '@material-ui/icons/CloudDownload';

import {
  ProfileChip,
  StatusChip,
  UserComment,
  DateTimeView,
  DocumentView,
  EventItem,
} from '../../general';

import localization from '../../../utils/localizations';
import lastStatus from '../../../utils/lastStatus';
import {
  Document,
  Status,
  ThesisUGB,
  Schedule,
} from '../../../types';

const useStyles = makeStyles((theme) => ({
  mainBody: {
    padding: 10,
  },
  mainGrid: {
    marginTop: 20,
  },
  gridLabelBolder: {
    textAlign: 'right',
    fontWeight: 500,
  },
  profileSpacing: {
    marginRight: 10,
  },
  marginVertical: {
    marginTop: 10,
    marginBottom: 10
  }
}));


function ThesisUGBDetail(props: {ugb: ThesisUGB}) {
  const classes = useStyles();
  let lastSubmittedStatus = lastStatus(props.ugb.status_history, 'submitted');
  let lastUpdatedStatus = lastStatus(props.ugb.status_history, props.ugb.status);
  return (
    <div>
      <Grid container spacing={3} className={classes.mainGrid}>
        <Grid item xs={4} sm={3}>
          <Typography variant="body1" className={classes.gridLabelBolder}>{localization.title}</Typography>
        </Grid>
        <Grid item xs={8} sm={9}>
          <Typography variant="body1">{props.ugb.title}</Typography>

          {props.ugb.advisor_research &&
            <div>
              <Chip
                size="small"
                label={localization.advisorResearch}
                color="primary"
                className={classes.marginVertical}
              />
            </div>}

        </Grid>
      </Grid>

      <Divider />
      <Grid container spacing={3} className={classes.mainGrid}>
        <Grid item xs={4} sm={3}>
          <Typography variant="body2" className={classes.gridLabelBolder}>{localization.undergraduateBackground}:</Typography>
        </Grid>
        <Grid item xs={8} sm={9}></Grid>
      </Grid>
      
      <Grid container spacing={3} className={classes.mainGrid}>
        <Grid item xs={4} sm={3}>
          <Typography variant="body1" className={classes.gridLabelBolder}>{localization.university}</Typography>
        </Grid>
        <Grid item xs={8} sm={9}>
          <Typography variant="body1">{props.ugb.s1_background_university}</Typography>
        </Grid>
      </Grid>

      <Grid container spacing={3} className={classes.mainGrid}>
        <Grid item xs={4} sm={3}>
          <Typography variant="body1" className={classes.gridLabelBolder}>{localization.universityProgram}</Typography>
        </Grid>
        <Grid item xs={8} sm={9}>
          <Typography variant="body1">{props.ugb.s1_background_program}</Typography>

          {props.ugb.s1_fast_track &&
            <div>
              <Chip
                size="small"
                label={localization.fastTrack}
                color="primary"
                className={classes.marginVertical}
              />
            </div>}

        </Grid>
      </Grid>

      <Grid container spacing={3} className={classes.mainGrid}>
        <Grid item xs={4} sm={3}>
          <Typography variant="body1" className={classes.gridLabelBolder}>{localization.finalProjectTitle}</Typography>
        </Grid>
        <Grid item xs={8} sm={9}>
          <Typography variant="body1">{props.ugb.s1_final_project_title}</Typography>
        </Grid>
      </Grid>

      <Grid container spacing={3} className={classes.mainGrid}>
        <Grid item xs={4} sm={3}>
          <Typography variant="body1" className={classes.gridLabelBolder}>{localization.finalProjectAdvisor}</Typography>
        </Grid>
        <Grid item xs={8} sm={9}>
          <Typography variant="body1">{props.ugb.s1_final_project_advisor}</Typography>
        </Grid>
      </Grid>

      <Grid container spacing={3} className={classes.mainGrid}>
        <Grid item xs={4} sm={3}>
          <Typography variant="body1" className={classes.gridLabelBolder}>{localization.publication}</Typography>
        </Grid>
        <Grid item xs={8} sm={9}>
          <Typography variant="body1">{props.ugb.s1_publication ? localization.yes : localization.no}</Typography>
        </Grid>
      </Grid>

      {props.ugb.s1_publication &&
      <Grid container spacing={3} className={classes.mainGrid}>
        <Grid item xs={4} sm={3}>
          <Typography variant="body1" className={classes.gridLabelBolder}>{localization.journalPublisher}</Typography>
        </Grid>
        <Grid item xs={8} sm={9}>
          <Typography variant="body1">{props.ugb.s1_publication_journal_publisher}</Typography>
        </Grid>
      </Grid>
      }

      {props.ugb.s1_publication &&
      <Grid container spacing={3} className={classes.mainGrid}>
        <Grid item xs={4} sm={3}>
          <Typography variant="body1" className={classes.gridLabelBolder}>{localization.author}</Typography>
        </Grid>
        <Grid item xs={8} sm={9}>
          <Typography variant="body1">{props.ugb.s1_publication_author}</Typography>
        </Grid>
      </Grid>
      }

      {props.ugb.s1_publication &&
      <Grid container spacing={3} className={classes.mainGrid}>
        <Grid item xs={4} sm={3}>
          <Typography variant="body1" className={classes.gridLabelBolder}>{localization.title}</Typography>
        </Grid>
        <Grid item xs={8} sm={9}>
          <Typography variant="body1">{props.ugb.s1_publication_title}</Typography>
        </Grid>
      </Grid>
      }

      <Divider />

      <Grid container spacing={3} className={classes.mainGrid}>
        <Grid item xs={4} sm={3}>
          <Typography variant="body1" className={classes.gridLabelBolder}>{localization.ugbStatus}</Typography>
        </Grid>
        <Grid item xs={8} sm={9}>
          <StatusChip status={props.ugb.status} size="small" />
          {lastUpdatedStatus?.comment?.comment &&
          <div>
            <Typography variant="subtitle2" className={classes.marginVertical}>{localization.comment}</Typography>
            <UserComment
              comment={lastUpdatedStatus.comment}
              inline
              hideUser
            />
          </div>
          }
        </Grid>
      </Grid>

      {lastSubmittedStatus &&
      <Grid container spacing={3} className={classes.mainGrid}>
        <Grid item xs={4} sm={3}>
          <Typography variant="body1" className={classes.gridLabelBolder}>{localization.ugbSubmittedAt}</Typography>
        </Grid>
        <Grid item xs={8} sm={9}>
          <Typography variant="body1"><DateTimeView value={lastSubmittedStatus.updated_at} chip /></Typography>
        </Grid>
      </Grid>
      }

      <Grid container spacing={3} className={classes.mainGrid}>
        <Grid item xs={4} sm={3}>
          <Typography variant="body1" className={classes.gridLabelBolder}>{localization.prospectivePrimaryAdvisor}</Typography>
        </Grid>
        <Grid item xs={8} sm={9}>
          {props.ugb.primary_advisor &&
            <ProfileChip profile={props.ugb.primary_advisor} />
          }
        </Grid>
      </Grid>

      <Grid container spacing={3} className={classes.mainGrid}>
        <Grid item xs={4} sm={3}>
          <Typography variant="body1" className={classes.gridLabelBolder}>{localization.additionalProspectiveAdvisors}</Typography>
        </Grid>
        <Grid item xs={8} sm={9}>
          {props.ugb.additional_advisors.map((advisor: any) => {
            return <ProfileChip profile={advisor} className={classes.profileSpacing} />
          })}
        </Grid>
      </Grid>

      {props.ugb.documents.map((doc: Document, j: number) => {
        let label = (localization as any)[doc.kind] ? (localization as any)[doc.kind] : doc.kind;
        let prevDoc = (j > 0) ? props.ugb.documents[j-1] : undefined;
        if (prevDoc && prevDoc.kind == doc.kind) label = '';
        return (
          <Grid container spacing={3} className={classes.mainGrid}>
            <Grid item xs={4} sm={3}>
              <Typography variant="body1" className={classes.gridLabelBolder}>{label}</Typography>
            </Grid>
            <Grid item xs={8} sm={9}>
              <DocumentView doc={doc} showSimilarity={doc.kind === 'UGB'} />
            </Grid>
          </Grid>
        );
      })}

      {(props.ugb.ugb_schedules.length > 0) &&
        <Grid container spacing={3} className={classes.mainGrid}>
          <Grid item xs={4} sm={3}>
            <Typography variant="body1" className={classes.gridLabelBolder}>{localization.schedule}</Typography>
          </Grid>
          <Grid item xs={8} sm={9}>
          {props.ugb.ugb_schedules.map((event: Schedule, j: number) => {
            return (
              <EventItem
                key={`schedule-event-${j}`}
                event={event}
              />
            );
          })}
          </Grid>
        </Grid>
      }
    </div>
    );
}

export {ThesisUGBDetail};